import React, { useMemo, useState } from 'react';
import ATable from 'components/Clinic/ClinicFinancials/ATable';
import { APlot } from 'components/Clinic/ClinicFinancials/APlot';
import SubcriptionsWorker from '../../../workers/subscriptions.worker?worker';
import useWebWorker from 'workers/useWebWorker';

export const Subscriptions = ({ subscriptions }) => {
  return (
    <div className="stats">
      <ActiveSubscriptionsTable subscriptions={subscriptions} />
      <SubscriptionsPlot subscriptions={subscriptions} />
      <CumulativeSubscriptionsPlot subscriptions={subscriptions} />
    </div>
  );
};

export const Unsubscriptions = ({ subscriptions, events, overdueSubscriptions }) => {
  return (
    <div className="">
      <div className="auto-two-cols">
        <UnsubscriptionsPlot subscriptions={subscriptions} events={events} />
        <div className="grid gap-[var(--inner-gap)]">
          <SubscriptionsEndedTable subscriptions={subscriptions} />
          <PaymentFailedTable subscriptions={subscriptions} />
          <SubscriptionEndedPatientUnsubscribedTable subscriptions={subscriptions} />
          <DelinquentsTable
            subscriptions={subscriptions}
            overdueSubscriptions={overdueSubscriptions}
          />
        </div>
      </div>
      <div className="auto-two-cols">
        <PatientsUnsubscribedTable subscriptions={subscriptions} />
        <UnsubscriptionReasonsTable events={events} />
      </div>
    </div>
  );
};

function UnsubscriptionReasonsTable({ events }) {
  const [data, setData] = useState();

  const message = useMemo(() => {
    return {
      action: 'getUnsubscriptionReasonsTableData',
      events,
    };
  }, [events]);

  function createTableData(values) {
    return {
      headers: ['Reason', 'Last Month', 'Month To Date', 'Total'],
      rows: values ?? [],
    };
  }

  const tableData = createTableData(data);

  useWebWorker(SubcriptionsWorker, message, setData);

  return (
    <ATable title="Unsubscription Reasons" headers={tableData.headers} rows={tableData.rows} />
  );
}

function PatientsUnsubscribedTable({ subscriptions }) {
  const [data, setData] = useState();

  const message = useMemo(() => {
    return {
      action: 'getPatientsUnsubscribedTableData',
      subscriptions,
      byPlan: true,
    };
  }, [subscriptions]);

  function createTableData(values) {
    return {
      headers: ['', 'Last Month', 'Month To Date', 'Month To End'],
      rows: values ?? [],
    };
  }

  const tableData = createTableData(data);

  useWebWorker(SubcriptionsWorker, message, setData);

  return (
    <ATable
      title="Patients Clicked Unsubscribe"
      headers={tableData.headers}
      rows={tableData.rows}
    />
  );
}

function SubscriptionEndedPatientUnsubscribedTable({ subscriptions }) {
  const [data, setData] = useState();

  const message = useMemo(() => {
    return {
      action: 'getUnsubscriptionsMonthlyTableData',
      subscriptions,
      byPlan: false,
    };
  }, [subscriptions]);

  function createTableData(values) {
    return {
      headers: ['', 'Last Month', 'Month To Date', 'Month To End'],
      rows: values ?? [],
    };
  }

  const tableData = createTableData(data);

  useWebWorker(SubcriptionsWorker, message, setData);

  return data && <ATable title="" headers={tableData.headers} rows={tableData.rows} />;
}

function SubscriptionsEndedTable({ subscriptions }) {
  const [data, setData] = useState();

  const message = useMemo(() => {
    return {
      action: 'getSubscriptionsEndedMonthlyTableData',
      subscriptions,
      byPlan: true,
    };
  }, [subscriptions]);

  function createTableData(values) {
    return {
      headers: ['', 'Last Month', 'Month To Date', 'Month To End'],
      rows: values ?? [],
    };
  }

  const tableData = createTableData(data);

  useWebWorker(SubcriptionsWorker, message, setData);

  return (
    data && <ATable title="Subscriptions Ended" headers={tableData.headers} rows={tableData.rows} />
  );
}

function PaymentFailedTable({ subscriptions }) {
  const [data, setData] = useState();

  const message = useMemo(() => {
    return {
      action: 'getSubscriptionsPaymentFailedMonthlyTableData',
      subscriptions,
      byPlan: false,
    };
  }, [subscriptions]);

  function createTableData(values) {
    return {
      headers: ['', 'Last Month', 'Month To Date', 'Month To End'],
      rows: values ?? [],
    };
  }

  const tableData = createTableData(data);

  useWebWorker(SubcriptionsWorker, message, setData);

  return data && <ATable title="" headers={tableData.headers} rows={tableData.rows} />;
}

export const ActiveSubscriptionsTable = ({ subscriptions }) => {
  const [data, setData] = useState();

  const workerMessage = useMemo(() => {
    if (subscriptions) {
      return { action: 'getActiveSubscriptionsTableValues', subscriptions };
    }
  }, [subscriptions]);

  useWebWorker(SubcriptionsWorker, workerMessage, setData);

  function createTableData(values) {
    return {
      headers: ['Plan', 'Interval', 'Total', 'Last Month', 'Month to Date'],
      rows: values ?? [],
    };
  }

  const tableData = createTableData(data);

  return (
    data && (
      <ATable title="Active Subscriptions" headers={tableData.headers} rows={tableData.rows} />
    )
  );
};

const ASubscriptionPlot2 = ({ datasets, lineTitle = 'Subscriptions' }) => {
  return <APlot datasets={datasets} />;
};

export const SubscriptionsPlot = ({ subscriptions }) => {
  const [datasets, setDatasets] = useState();
  const message = useMemo(() => {
    return {
      action: 'getActiveSubscriptionsPlotData',
      subscriptions,
    };
  }, [subscriptions]);

  useWebWorker(SubcriptionsWorker, message, setDatasets);

  //TODO show loading
  if (!datasets) {
    return null;
  }

  return <ASubscriptionPlot2 lineTitle="New Subscriptions" datasets={datasets} />;
};

export const CumulativeSubscriptionsPlot = ({ subscriptions }) => {
  const [datasets, setDatasets] = useState();
  const message = useMemo(() => {
    return {
      action: 'getCumulativeSubscriptionsPlotData',
      subscriptions,
    };
  }, [subscriptions]);

  useWebWorker(SubcriptionsWorker, message, setDatasets);

  //TODO show loading
  if (!datasets) {
    return null;
  }

  return <ASubscriptionPlot2 lineTitle="Active Subscriptions" datasets={datasets} />;
};

export const UnsubscriptionsPlot = ({ subscriptions }) => {
  const [datasets, setDatasets] = useState();

  const message = useMemo(() => {
    return {
      action: 'getSubscriptionsEndedPlotData',
      subscriptions,
    };
  }, [subscriptions]);

  useWebWorker(SubcriptionsWorker, message, setDatasets);

  if (!datasets) {
    return null;
  }

  return <ASubscriptionPlot2 lineTitle="Subscriptions Ended" datasets={datasets} />;
};

export const DelinquentsTable = ({ subscriptions, overdueSubscriptions }) => {
  const [data, setData] = useState();
  const message = useMemo(() => {
    return {
      action: 'getDelinquents',
      subscriptions,
      overdueSubscriptions,
    };
  }, [subscriptions, overdueSubscriptions]);

  useWebWorker(SubcriptionsWorker, message, setData);

  if (!data) {
    return null;
  }

  const tableData = {
    headers: ['Status', 'Total', 'Last Month', 'Month to Date'],
    rows: data ?? [],
  };

  return data && <ATable title="Delinquents" headers={tableData.headers} rows={tableData.rows} />;
};
